import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Typography,
  Paper,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Chip,
  Button,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Badge,
  Stack
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import SearchIcon from '@mui/icons-material/Search';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import { Customer, UserProfile, getUsersByCustomer } from '../../firebase/services';

interface CustomersListProps {
  customers: Customer[];
  users: UserProfile[];
  loading: boolean;
  onLinkUser?: (userId: string, customerId: string) => Promise<boolean>;
}

const CustomersList: React.FC<CustomersListProps> = ({ customers, users, loading, onLinkUser }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [customerUsers, setCustomerUsers] = useState<{[key: string]: UserProfile[]}>({});
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showUsersDialog, setShowUsersDialog] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // Fetch users for each customer
  useEffect(() => {
    const fetchUsersForCustomers = async () => {
      const usersMap: {[key: string]: UserProfile[]} = {};
      
      for (const customer of customers) {
        try {
          const customerUsersList = await getUsersByCustomer(customer.id);
          usersMap[customer.id] = customerUsersList;
        } catch (error) {
          console.error(`Error fetching users for customer ${customer.id}:`, error);
          usersMap[customer.id] = [];
        }
      }
      
      setCustomerUsers(usersMap);
    };
    
    if (customers.length > 0) {
      fetchUsersForCustomers();
    }
  }, [customers]);
  
  // Filtrer les clients en fonction du terme de recherche
  const filteredCustomers = customers.filter(customer => {
    const searchTermLower = searchTerm.toLowerCase();
    return (
      customer.name.toLowerCase().includes(searchTermLower) ||
      customer.companyName.toLowerCase().includes(searchTermLower) ||
      (customer.email && customer.email.toLowerCase().includes(searchTermLower))
    );
  });

  // Formater la date
  const formatDate = (date: any) => {
    if (!date) return '-';
    
    const jsDate = date.toDate ? date.toDate() : new Date(date);
    
    return new Intl.DateTimeFormat('fr-FR', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(jsDate);
  };

  // Handle showing users for a customer
  const handleShowUsers = (customer: Customer) => {
    setSelectedCustomer(customer);
    setShowUsersDialog(true);
  };

  // Handle showing link user dialog
  const handleShowLinkDialog = (customer: Customer) => {
    setSelectedCustomer(customer);
    setShowLinkDialog(true);
  };

  // Handle linking a user to a customer
  const handleLinkUser = async (userId: string) => {
    if (!selectedCustomer || !onLinkUser) return;
    
    setLoadingUsers(true);
    try {
      const success = await onLinkUser(userId, selectedCustomer.id);
      if (success) {
        // Refresh the users list for this customer
        const updatedUsers = await getUsersByCustomer(selectedCustomer.id);
        setCustomerUsers(prev => ({
          ...prev,
          [selectedCustomer.id]: updatedUsers
        }));
        
        // Trouver l'utilisateur pour l'affichage
        const linkedUser = users.find(u => u.id === userId);
        
        // Afficher un message de succès
        console.log(`User ${linkedUser?.displayName || userId} linked to customer ${selectedCustomer.companyName}`);
      } else {
        console.error("Failed to link user to customer:", userId, selectedCustomer.id);
      }
    } catch (error) {
      console.error("Error linking user:", error);
    } finally {
      setLoadingUsers(false);
      setShowLinkDialog(false);
    }
  };

  // Get unlinked users that can be linked to a customer
  const getUnlinkedUsers = () => {
    if (!selectedCustomer) return [];
    
    const linkedUserIds = (customerUsers[selectedCustomer.id] || []).map(user => user.id);
    return users.filter(user => 
      !linkedUserIds.includes(user.id) && 
      (!user.customerId || user.customerId === '')
    );
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 3, 
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Typography 
        variant="h6" 
        sx={{ 
          mb: 3,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        <BusinessIcon />
        Liste des clients
      </Typography>
      
      {/* Ticket Statistics */}
      <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
        {/* Replace MuiGrid with Stack */}
        
      </Stack> 

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack spacing={2}>
          {/* Replace MuiGrid with Stack */}
          <TextField
            fullWidth
            placeholder="Rechercher un client..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            variant="outlined"
            size="small"
          />
        </Stack>
      </Paper>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Entreprise</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Téléphone</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Utilisateurs</TableCell>
                <TableCell>Créé le</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCustomers.length > 0 ? (
                filteredCustomers.map((customer) => (
                  <TableRow key={customer.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {customer.companyName}
                      </Typography>
                    </TableCell>
                    <TableCell>{customer.name}</TableCell>
                    <TableCell>{customer.email || '-'}</TableCell>
                    <TableCell>{customer.phone || '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={customer.status === 'active' ? 'Actif' : 'Inactif'} 
                        color={customer.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Badge 
                        badgeContent={customerUsers[customer.id]?.length || 0} 
                        color="primary"
                        showZero
                      >
                        <IconButton 
                          size="small" 
                          onClick={() => handleShowUsers(customer)}
                          color="primary"
                        >
                          <PeopleIcon />
                        </IconButton>
                      </Badge>
                    </TableCell>
                    <TableCell>{formatDate(customer.createdAt)}</TableCell>
                    <TableCell>
                      <Tooltip title="Gérer le client">
                        <IconButton 
                          size="small"
                          component={Link}
                          to={`/admin/customer/${customer.id}`}
                          color="primary"
                          sx={{ mr: 1 }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Lier un utilisateur">
                        <IconButton 
                          size="small"
                          onClick={() => handleShowLinkDialog(customer)}
                          color="primary"
                        >
                          <PersonAddIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    {searchTerm ? 'Aucun client ne correspond à votre recherche' : 'Aucun client trouvé'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog to show users linked to a customer */}
      <Dialog 
        open={showUsersDialog} 
        onClose={() => setShowUsersDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Utilisateurs de {selectedCustomer?.companyName}
        </DialogTitle>
        <DialogContent dividers>
          {customerUsers[selectedCustomer?.id || '']?.length > 0 ? (
            <List>
              {customerUsers[selectedCustomer?.id || '']?.map(user => (
                <ListItem key={user.id}>
                  <Avatar sx={{ mr: 2 }}>{user.displayName?.charAt(0) || 'U'}</Avatar>
                  <ListItemText 
                    primary={user.displayName} 
                    secondary={user.email} 
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body1" align="center" sx={{ py: 2 }}>
              Aucun utilisateur lié à ce client
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUsersDialog(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog to link a user to a customer */}
      <Dialog 
        open={showLinkDialog} 
        onClose={() => setShowLinkDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Lier un utilisateur à {selectedCustomer?.companyName}
        </DialogTitle>
        <DialogContent dividers>
          {loadingUsers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : getUnlinkedUsers().length > 0 ? (
            <List>
              {getUnlinkedUsers().map(user => (
                <ListItem 
                  key={user.id}
                  onClick={() => handleLinkUser(user.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <Avatar sx={{ mr: 2 }}>{user.displayName?.charAt(0) || 'U'}</Avatar>
                  <ListItemText 
                    primary={user.displayName} 
                    secondary={user.email} 
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body1" align="center" sx={{ py: 2 }}>
              Aucun utilisateur disponible à lier
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLinkDialog(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default CustomersList; 